"use client";

import Link from "next/link";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { fuzzyDictionarySearch } from "@/lib/blog/dictionary-search";
import type { BlogDictionaryGroup } from "@/lib/server/blog/types";
import type { Locale } from "@/lib/i18n/config";
import { localizeBlogHref } from "@/lib/i18n/routing";
import { cn } from "@/lib/utils";

const SEARCH_DEBOUNCE_MS = 120;

export default function DictionaryIndex({
  groups,
  locale,
  searchPlaceholder,
  noResultsLabel,
}: {
  groups: BlogDictionaryGroup[];
  locale: Locale;
  searchPlaceholder: string;
  noResultsLabel: string;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query]);

  const filteredGroups = useMemo(() => {
    const normalized = debouncedQuery.trim();
    if (!normalized) {
      return groups;
    }

    return groups
      .map((group) => ({
        ...group,
        terms: group.terms
          .map((term) => ({
            term,
            match: fuzzyDictionarySearch(normalized, term.title),
          }))
          .filter(({ match }) => match.match)
          .sort(
            (a, b) =>
              b.match.relevance - a.match.relevance ||
              b.match.score - a.match.score,
          )
          .map(({ term }) => term),
      }))
      .filter((group) => group.terms.length > 0);
  }, [groups, debouncedQuery]);

  const hasResults = filteredGroups.length > 0;
  const isSearching = debouncedQuery.trim().length > 0;

  return (
    <div className="space-y-12">
      <label className="relative block w-full">
        <span className="sr-only">{searchPlaceholder}</span>
        <Search
          className="pointer-events-none absolute start-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          type="text"
          name="query"
          value={query}
          autoComplete="off"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          className="w-full rounded-2xl bg-secondary py-3.5 ps-11 pe-4 text-base text-foreground outline-none placeholder:text-muted-foreground focus:shadow-[0_0_0_3px_color-mix(in_srgb,#008cff_18%,transparent)]"
        />
      </label>

      {isSearching && !hasResults ? (
        <p className="text-sm text-muted-foreground">{noResultsLabel}</p>
      ) : null}

      <div className="space-y-14">
        {filteredGroups.map((group) => (
          <section
            key={group.letter}
            className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-8 lg:gap-12"
          >
            <div className="shrink-0 sm:sticky sm:top-[calc(var(--nav-height)+var(--header-float-gap)+2rem)] sm:w-14 lg:w-16">
              <span className="font-heading text-4xl font-black leading-none text-[#008cff] md:text-5xl">
                {group.letter}
              </span>
            </div>

            <div className="grid min-w-0 flex-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {group.terms.map((term) => (
                <Link
                  key={term.id}
                  href={localizeBlogHref(term.href, locale)}
                  prefetch={false}
                  className={cn(
                    "service-mesh-surface group flex h-full flex-col overflow-hidden rounded-2xl p-5 transition-opacity hover:opacity-95",
                  )}
                >
                  <h3 className="relative z-[1] font-heading text-base font-semibold leading-snug text-foreground">
                    {term.title}
                  </h3>
                  {term.excerpt ? (
                    <p className="relative z-[1] mt-2 line-clamp-3 text-sm leading-6 text-muted-foreground">
                      {term.excerpt}
                    </p>
                  ) : null}
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
